/**
 * FormActions Component - Standardized button container for form submission actions
 * Consistently places Cancel button on left and Save/Submit button on right
 * Replaces scattered form-actions CSS patterns across the application
 * 
 * Usage:
 * <FormActions onCancel={handleCancel}>
 *   <Button type="submit" variant="primary">Save</Button>
 *   <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
 * </FormActions>
 * 
 * OR simplified usage:
 * <FormActions 
 *   onCancel={handleCancel}
 *   submitLabel="Save"
 *   cancelLabel="Cancel"
 *   onSubmit={handleSubmit}
 *   isSubmitting={isSubmitting}
 * />
 */

import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import Button from './Button';
import styles from './FormActions.module.css';

const FormActions = ({
  // Option 1: Custom children (Button components)
  children,

  // Option 2: Simplified props
  onCancel,
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isSubmitting = false,
  disabled = false,
  showCancel = true,

  // Customization
  submitVariant = 'primary',
  cancelVariant = 'secondary',
  gap = 'md', // 'sm', 'md', 'lg'
  className = '',
  style = {},
  submitButtonProps = {},
  cancelButtonProps = {},
  ...props
}) => {
  const { theme } = useTheme();

  const getGap = () => {
    switch (gap) {
      case 'sm':
        return theme.spacing.sm;
      case 'lg':
        return theme.spacing.lg;
      default:
        return theme.spacing.md;
    }
  };

  const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: getGap(),
    flexWrap: 'wrap',
    marginTop: theme.spacing.xl,
    paddingTop: theme.spacing.lg,
    borderTop: `1px solid ${theme.colors.borderLight || theme.colors.border}`,
    background: 'transparent', // Ensure no white background
    ...style
  };

  // If children are provided, use them directly
  if (children && React.Children.count(children) > 0) {
    return (
      <div
        className={`form-actions ${styles.formActions} ${className}`}
        style={baseStyles}
        {...props}
      >
        {children}
      </div>
    );
  }

  // Otherwise, render simplified version with standard Cancel/Submit buttons
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (onSubmit) onSubmit(e);
      }}
      className={`form-actions-container ${className}`}
      style={style}
      {...props}
    >
      <div
        className={`form-actions ${styles.formActions}`}
        style={baseStyles}
      >
        {/* Cancel button on left */}
        {showCancel && (
          <Button
            type="button"
            variant={cancelVariant}
            onClick={onCancel}
            disabled={isSubmitting || disabled}
            {...cancelButtonProps}
          >
            {cancelLabel}
          </Button>
        )}

        {/* Submit button on right */}
        <Button
          type="submit"
          variant={submitVariant}
          disabled={isSubmitting || disabled}
          loading={isSubmitting}
          {...submitButtonProps}
        >
          {isSubmitting ? `${submitLabel}...` : submitLabel}
        </Button>
      </div>
    </form>
  );
};

export default FormActions;
